// Centralized TanStack QueryClient.
//
// Defaults are deliberately conservative so downstream tasks can override
// per-query (e.g. real-time stream views will want staleTime: 0). The
// global error handler surfaces a user-facing toast without leaking any
// auth/header internals — 401 navigation lives in the api-client, not here.
import { QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-client'

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    // 401s already trigger logout/redirect via the api-client; suppress the
    // toast so the user doesn't see "Unauthorized" right before navigating.
    if (err.status === 401) return ''
    return err.message
  }
  if (err instanceof Error) {
    return err.message || 'Request failed.'
  }
  return 'Request failed.'
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      const msg = describeError(err)
      if (msg) toast.error(msg)
    },
  }),
  defaultOptions: {
    queries: {
      // 30s baseline; per-query overrides are encouraged for real-time data.
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Mutations should not retry by default — let callers opt in.
      retry: 0,
    },
  },
})
